import { Component, OnInit, OnDestroy, ViewChild, ElementRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, takeUntil, switchMap } from 'rxjs/operators';
import { trigger, transition, style, animate } from '@angular/animations';
import { CustomerService } from '../../services/customer.service';
import { VoucherService } from '../../services/voucher.service';
import { sanitizeSearchTerm } from '../../utils/search.utils';
import { SmsService } from '../../services/sms.service';
import { PermissionService } from '../../services/permission.service';
import { UserPermission } from '../../models/permission.model';
import { PartnerCustomerSearchDto } from '../../models/customer.model';
import { VoucherDto, VoucherStatus, getVoucherStatusLabel, RevokeVouchersDto } from '../../models/voucher.model';
import { VoucherFilterParams } from '../../models/voucher-filter.model';
import { OtpVerificationComponent } from '../shared/otp-verification/otp-verification.component';

@Component({
    selector: 'app-voucher-revoke',
    standalone: true,
    imports: [CommonModule, FormsModule, OtpVerificationComponent],
    templateUrl: './voucher-revoke.component.html',
    styleUrl: './voucher-revoke.component.css',
    animations: [
        trigger('slideDown', [
            transition(':enter', [
                style({ height: 0, opacity: 0, overflow: 'hidden' }),
                animate('300ms ease-out', style({ height: '*', opacity: 1 }))
            ]),
            transition(':leave', [
                animate('200ms ease-in', style({ height: 0, opacity: 0, overflow: 'hidden' }))
            ])
        ])
    ]
})
export class VoucherRevokeComponent implements OnInit, OnDestroy {
    @ViewChild('searchInput') searchInput!: ElementRef<HTMLInputElement>;
    @ViewChild(OtpVerificationComponent) otpComponent!: OtpVerificationComponent;

    // Customer Search State
    searchTerm: string = '';
    customers: PartnerCustomerSearchDto[] = [];
    totalCount: number = 0;
    pageIndex: number = 0;
    pageSize: number = 10;
    isSearching: boolean = false;
    searchErrorMessage: string = '';
    hasSearched: boolean = false;

    // Selected Customer State
    selectedCustomer: PartnerCustomerSearchDto | null = null;

    // Redemption Reference Search State
    redemptionReference: string = '';
    isLoadingVouchers: boolean = false;
    vouchersErrorMessage: string = '';

    // Vouchers State
    vouchers: VoucherDto[] = [];
    selectedVoucherId: string | null = null;

    // Revoke State
    revokeNotes: string = '';
    isRevoking: boolean = false;
    revokeErrorMessage: string = '';
    revokeSuccessMessage: string = '';

    // OTP Verification State
    showOtpSection: boolean = false;
    otpVerified: boolean = false;

    // Expose enum to template
    VoucherStatus = VoucherStatus;

    private searchSubject = new Subject<string>();
    private destroy$ = new Subject<void>();

    constructor(
        private customerService: CustomerService,
        private voucherService: VoucherService,
        private smsService: SmsService,
        private permissionService: PermissionService,
        private router: Router
    ) { }

    ngOnInit(): void {
        // Setup debounced search for customers
        this.searchSubject
            .pipe(
                debounceTime(400),
                distinctUntilChanged(),
                takeUntil(this.destroy$),
                switchMap(term => {
                    const sanitized = sanitizeSearchTerm(term);
                    if (sanitized.length < 2) {
                        this.customers = [];
                        this.totalCount = 0;
                        this.hasSearched = false;
                        this.isSearching = false;
                        return [];
                    }

                    this.isSearching = true;
                    this.searchErrorMessage = '';
                    this.hasSearched = true;
                    return this.customerService.searchCustomers(sanitized, this.pageIndex, this.pageSize);
                })
            )
            .subscribe({
                next: (response) => {
                    if (response) {
                        this.customers = response.items;
                        this.totalCount = response.totalCount;
                    }
                    this.isSearching = false;
                    // Keep focus on search input after results load
                    setTimeout(() => {
                        this.searchInput?.nativeElement?.focus();
                    }, 0);
                },
                error: (error) => {
                    console.error('Error searching customers:', error);
                    this.searchErrorMessage = 'Failed to search customers. Please try again.';
                    this.isSearching = false;
                    this.customers = [];
                    this.totalCount = 0;
                }
            });
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    /**
     * Handle customer search input change
     */
    onSearchChange(value: string): void {
        this.searchTerm = value;
        this.pageIndex = 0;
        this.searchSubject.next(value);

        // Reset selected customer when search changes
        this.selectedCustomer = null;
        this.vouchers = [];
        this.redemptionReference = '';
    }

    /**
     * Select a customer
     */
    selectCustomer(customer: PartnerCustomerSearchDto): void {
        this.selectedCustomer = customer;
        this.vouchers = [];
        this.redemptionReference = '';
        this.vouchersErrorMessage = '';
        this.revokeErrorMessage = '';
        this.revokeSuccessMessage = '';
    }

    /**
     * Clear selected customer and go back to search
     */
    clearCustomer(): void {
        this.selectedCustomer = null;
        this.vouchers = [];
        this.redemptionReference = '';
        this.vouchersErrorMessage = '';
        this.revokeErrorMessage = '';
        this.revokeSuccessMessage = '';
        this.showOtpSection = false;
        this.otpVerified = false;
        if (this.otpComponent) {
            this.otpComponent.reset();
        }
    }

    /**
     * Search for vouchers by redemption reference
     */
    searchVouchersByReference(): void {
        if (!this.selectedCustomer || !this.redemptionReference.trim()) {
            this.vouchersErrorMessage = 'Please enter a redemption reference';
            return;
        }

        this.isLoadingVouchers = true;
        this.vouchersErrorMessage = '';
        this.revokeErrorMessage = '';
        this.revokeSuccessMessage = '';

        const filters: VoucherFilterParams = {
            redemptionReference: this.redemptionReference.trim(),
            status: VoucherStatus.Redeemed, // Only fetch redeemed vouchers as only they can be revoked
            pageIndex: 0,
            pageSize: 100 // Get all matching vouchers
        };

        // Call appropriate API based on permissions
        const voucherObservable = this.permissionService.hasPermission(UserPermission.Partner_ViewVouchers)
            ? this.voucherService.getPartnerVouchers(filters)
            : this.voucherService.getOutletVouchers(filters);

        voucherObservable
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: (response) => {
                    // Filter vouchers to only show those for the selected customer
                    this.vouchers = response.items.filter(v => v.customerId === this.selectedCustomer!.customerId);

                    if (this.vouchers.length === 0) {
                        this.vouchersErrorMessage = 'No vouchers with Redeemed status found with this redemption reference for the selected customer';
                    }

                    this.isLoadingVouchers = false;
                },
                error: (error) => {
                    console.error('Error loading vouchers:', error);
                    this.vouchersErrorMessage = error.error?.message || 'Failed to load vouchers. Please try again.';
                    this.isLoadingVouchers = false;
                    this.vouchers = [];
                }
            });
    }

    /**
     * Select a voucher for revocation
     */
    selectVoucher(voucherId: string | undefined): void {
        this.selectedVoucherId = voucherId || null;
        this.revokeErrorMessage = '';
        this.revokeSuccessMessage = '';
    }

    /**
     * Check if voucher is selected
     */
    isVoucherSelected(voucherId: string | undefined): boolean {
        return this.selectedVoucherId === voucherId;
    }

    /**
     * Check if voucher can be revoked (must be redeemed)
     */
    canRevokeVoucher(voucher: VoucherDto): boolean {
        return voucher.status === VoucherStatus.Redeemed;
    }

    /**
     * Check if there are any revokable vouchers in the list
     */
    hasRevokableVouchers(): boolean {
        return this.vouchers.some(v => this.canRevokeVoucher(v));
    }

    /**
     * Initiate revoke process - show OTP section
     */
    initiateRevoke(): void {
        if (!this.selectedCustomer || !this.redemptionReference.trim()) {
            this.revokeErrorMessage = 'Customer and redemption reference are required';
            return;
        }

        if (!this.hasRevokableVouchers()) {
            this.revokeErrorMessage = 'No revokable vouchers found';
            return;
        }

        // Show OTP section and trigger OTP send
        this.showOtpSection = true;
        this.otpVerified = false;
        this.revokeErrorMessage = '';
        this.revokeSuccessMessage = '';
    }

    /**
     * Handle OTP verification success
     */
    onOtpVerified(): void {
        this.otpVerified = true;
        this.revokeErrorMessage = '';
        this.revokeSuccessMessage = 'OTP Verified! You can now proceed with revocation.';

        // Don't auto-proceed - wait for user to review notes and click revoke button
    }

    /**
     * Handle OTP verification error
     */
    onOtpError(error: string): void {
        this.revokeErrorMessage = error;
    }

    /**
     * Close OTP section
     */
    closeOtpSection(): void {
        this.showOtpSection = false;
        this.otpVerified = false;
        if (this.otpComponent) {
            this.otpComponent.reset();
        }
    }

    /**
     * Revoke the vouchers (called when user clicks Revoke button after OTP verification)
     */
    revokeVoucher(): void {
        if (!this.selectedCustomer || !this.redemptionReference.trim()) {
            this.revokeErrorMessage = 'Customer and redemption reference are required';
            return;
        }

        const revokeRequest: RevokeVouchersDto = {
            customerId: this.selectedCustomer.customerId,
            redemptionReference: this.redemptionReference.trim(),
            notes: this.revokeNotes.trim() || undefined
        };

        this.isRevoking = true;
        this.revokeErrorMessage = '';
        this.revokeSuccessMessage = '';

        this.voucherService.revokeVouchers(revokeRequest)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: (revokedVouchers) => {
                    console.log('Vouchers revoked successfully:', revokedVouchers);
                    this.isRevoking = false;
                    this.revokeSuccessMessage = `Successfully revoked ${revokedVouchers.length} voucher(s)`;

                    // Clear the form and voucher list after successful revoke
                    setTimeout(() => {
                        this.redemptionReference = '';
                        this.vouchers = [];
                        this.revokeNotes = '';
                        this.selectedVoucherId = null;
                        this.revokeSuccessMessage = '';
                        this.showOtpSection = false;
                        this.otpVerified = false;
                        if (this.otpComponent) {
                            this.otpComponent.reset();
                        }
                    }, 1500);
                },
                error: (error) => {
                    console.error('Error revoking vouchers:', error);
                    this.isRevoking = false;
                    this.revokeErrorMessage = error.error?.message || 'Failed to revoke vouchers. Please try again.';
                }
            });
    }

    /**
     * Load more customers
     */
    loadMore(): void {
        if (this.customers.length < this.totalCount && !this.isSearching) {
            this.pageIndex++;
            this.isSearching = true;
            this.searchErrorMessage = '';

            this.customerService.searchCustomers(sanitizeSearchTerm(this.searchTerm), this.pageIndex, this.pageSize)
                .pipe(takeUntil(this.destroy$))
                .subscribe({
                    next: (response) => {
                        this.customers = [...this.customers, ...response.items];
                        this.totalCount = response.totalCount;
                        this.isSearching = false;
                    },
                    error: (error) => {
                        console.error('Error loading more customers:', error);
                        this.searchErrorMessage = 'Failed to load more customers. Please try again.';
                        this.isSearching = false;
                        this.pageIndex--;
                    }
                });
        }
    }

    /**
     * Check if there are more customer results
     */
    get hasMoreResults(): boolean {
        return this.customers.length < this.totalCount;
    }

    /**
     * Get status label for display
     */
    getStatusLabel(status?: VoucherStatus): string {
        return getVoucherStatusLabel(status);
    }

    /**
     * Get status CSS class
     */
    getStatusClass(status?: VoucherStatus): string {
        switch (status) {
            case VoucherStatus.Active:
                return 'status-active';
            case VoucherStatus.Redeemed:
                return 'status-redeemed';
            case VoucherStatus.Expired:
                return 'status-expired';
            case VoucherStatus.Cancelled:
                return 'status-cancelled';
            case VoucherStatus.Revoked:
                return 'status-revoked';
            default:
                return '';
        }
    }

    /**
     * Format date for display
     */
    formatDate(dateString?: string): string {
        if (!dateString) return 'N/A';
        const date = new Date(dateString);
        return date.toLocaleDateString('en-IN', {
            day: 'numeric',
            month: 'short',
            year: 'numeric'
        });
    }

    /**
     * Format date and time for display
     */
    formatDateTime(dateString?: string): string {
        if (!dateString) return 'N/A';
        const date = new Date(dateString);
        return date.toLocaleString('en-IN', {
            day: 'numeric',
            month: 'short',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    }
}
